'use client';

import {
	BigNum,
	decodeName,
	SpotMarketConfig,
	User,
	UserAccount,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	JLP_POOL_ID,
	createWithdrawIx,
	getUserKey,
} from '@velocity-exchange/common';
import { useCallback } from 'react';
import { ComputeUnits } from 'src/@types/types';
import { isAmplifyAccountName } from 'src/utils/amplify';
import captureException from 'src/utils/captureException';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { getOracleCrankIxs } from 'src/utils/oracle';
import { updateAfterTx } from 'src/stores/VelocityStore/helpers';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import { accountTradingDataQueryKeys } from 'src/hooks/query/accountTradingData/accountTradingDataQueryKeys';
import { PositionsByUserKey } from 'src/hooks/query/accountTradingData/utils/types';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';
import { useRiskCeilingSlotClock } from 'src/hooks/useSlotClock';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { JLP_POOL_JLP_MARKET_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import useVelocityClient from '../../useVelocityClient';
import invariant from 'tiny-invariant';
import useCurrentUserAccount from '../../useCurrentUserAccount';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import { useQueryClient } from '@tanstack/react-query';
import type { WalletName } from '@solana/wallet-adapter-base';

interface WithdrawCollateralParams {
	amount: BigNum;
	spotMarketConfig: SpotMarketConfig;
	userAccount?: UserAccount;
	isBorrow?: boolean;
	isMax?: boolean;
}

export function useWithdrawCollateral() {
	const velocityClient = useVelocityClient();
	const currentUserAccount = useCurrentUserAccount();
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const currentSlotRef = useCurrentSlotRef();
	const slotDurationMsRef = useSyncedRef(
		useRiskCeilingSlotClock().slotDurationMs
	);

	const accountsGet = useVelocityAccountsStore((s) => s.get);
	const queryClient = useQueryClient();
	const getTxParams = useGetTxParams();

	const withdrawAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			userClient: User,
			amount: BigNum,
			spotMarketConfig: SpotMarketConfig,
			isBorrow: boolean,
			isMax: boolean,
			allActiveMarketIds: MarketId[],
			walletName: WalletName<string>,
			currentSlot: number
		) => {
			invariant(velocityClient, 'driftClient is required');

			const optionalIxs = await getOracleCrankIxs(
				allActiveMarketIds,
				velocityClient,
				walletName,
				{ current: currentSlot, durationMs: slotDurationMsRef.current },
				undefined,
				2
			);

			const withdrawIxs = await createWithdrawIx({
				velocityClient: velocityClient,
				user: userClient,
				amount: amount.shiftTo(spotMarketConfig.precisionExp),
				spotMarketConfig,
				isBorrow,
				isMax,
			});

			const allIxs = [...optionalIxs, ...withdrawIxs];

			const txParams = getTxParams({
				marketId: MarketId.createSpotMarket(spotMarketConfig.marketIndex),
				computeUnits: 1_200_000 as ComputeUnits,
				lowerBoundCu: 450_000,
			});

			await wallet.signAndSendTransaction(allIxs, txParams);
		},
		[velocityClient, getTxParams]
	);

	const wrappedWithdraw =
		useSponsoredSignVelocityActionWithFallback(withdrawAction);

	const withdrawCollateral = useCallback(
		async ({
			amount,
			spotMarketConfig,
			userAccount,
			isBorrow = false,
			isMax = false,
		}: WithdrawCollateralParams) => {
			invariant(velocityClient, 'Failed to get drift client');

			if (!userAccount) {
				userAccount = currentUserAccount?.client?.getUserAccount();
			}

			// can't withdraw from a delegate account

			const walletPubkey = walletAdapter?.publicKey;
			if (
				!velocityClient ||
				!walletAdapter ||
				!walletPubkey ||
				!userAccount ||
				!userAccount.authority.equals(walletPubkey)
			)
				return;

			const toastId = `${
				isBorrow ? 'borrow' : 'withdraw'
			}-${amount.toString()}-${Date.now()}`;

			// cant open a borrow on the deposit asset in an amplify account
			if (
				userAccount.poolId === JLP_POOL_ID &&
				isAmplifyAccountName(decodeName(userAccount.name))
			) {
				if (
					spotMarketConfig.marketIndex === JLP_POOL_JLP_MARKET_INDEX &&
					isBorrow
				) {
					notify({
						type: 'error',
						message: 'Invalid Amplify Position',
						id: toastId,
					});
					return false;
				}
			}

			await velocityClient.switchActiveUser(
				userAccount.subAccountId,
				userAccount.authority
			);

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			const userKey = getUserKey(
				userAccount.subAccountId,
				userAccount.authority
			);
			const accountData = accountsGet().accounts[userKey];

			const positionsData = queryClient.getQueryData<PositionsByUserKey>(
				accountTradingDataQueryKeys.positions()
			);
			const perpMarketIds = (
				positionsData?.[userKey]?.openPerpPositions ?? []
			).map((position) => MarketId.createPerpMarket(position.marketIndex));

			const spotMarketIds =
				accountData?.spotBalances.map((balance) =>
					MarketId.createSpotMarket(balance.asset.marketIndex)
				) ?? [];

			const allActiveMarketIds = [...perpMarketIds, ...spotMarketIds];

			const walletName = walletAdapter?.name;
			const userClient = accountsGet().accounts[userKey]?.client;

			if (!walletName || !userClient) {
				throw new Error('Missing required dependencies for withdrawal');
			}

			try {
				await wrappedWithdraw(
					userClient,
					amount,
					spotMarketConfig,
					isBorrow,
					isMax,
					allActiveMarketIds,
					walletName,
					currentSlotRef.current
				);

				velocityClient.spotMarketLastSlotCache.set(
					spotMarketConfig.marketIndex,
					currentSlotRef.current
				);

				notify({
					type: 'success',
					message: `${isBorrow ? 'Borrow' : 'Withdraw'} Successful.`,
					id: toastId,
					updatePrevious: true,
				});

				const userClientForUpdate = accountData?.client;

				if (!userClientForUpdate) throw new Error('Failed to get user client');

				updateAfterTx(velocityClient, userClientForUpdate);

				return true;
			} catch (e: any) {
				console.error(e);
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
				return false;
			}
		},
		[
			velocityClient,
			walletAdapter,
			currentUserAccount,
			accountsGet,
			wrappedWithdraw,
		]
	);

	return { withdrawCollateral };
}
